import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Approver } from '@/app/models/approvers';
import { ProjectResponse } from '@/app/models/projects';
import { useApproversQuery } from '@/app/services/approvers';
import { useUpdateProjectsMutation } from '@/app/services/projectsApi';
import { formatDateForBackend, parseCurrency } from '@/app/utils/dateUtils';
import { formatCurrency } from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import useFormattedCurrency from '../../hooks/useFormattedCurrency';
import FormField from './baseForms/FormField';
import FormFieldLong from './baseForms/FormFieldLong';
import FormFieldSelect from './baseForms/FormFieldSelect';

interface NewProjectsSettingsScreenProps {
  setDrawerSendAttachmentEight: (open: boolean) => void;
  selectOneProject: ProjectResponse | null;
}

export default function NewProjectsSettingsScreen(props: NewProjectsSettingsScreenProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['projects', 'common']);
  const { setDrawerSendAttachmentEight, selectOneProject } = props;

  const [plannedInitDate, setPlannedInitDate] = useState<string>(
    selectOneProject?.plannedInitDate ? dayjs(selectOneProject.plannedInitDate).format('DD/MM/YYYY') : '',
  );
  const [plannedEndDate, setPlannedEndDate] = useState<string>(
    selectOneProject?.plannedEndDate ? dayjs(selectOneProject.plannedEndDate).format('DD/MM/YYYY') : '',
  );

  const { value: initialBalance, setValue: setInitialBalance } = useFormattedCurrency(selectOneProject?.initialBalance);

  const [manager, setManager] = useState<Approver>(selectOneProject?.manager as Approver);
  const [justification, setJustification] = useState<string>(selectOneProject?.justify ? selectOneProject.justify : '');

  const [update] = useUpdateProjectsMutation();

  const { data } = useApproversQuery({ searchTerm: '', project: selectOneProject?.code || '', page: 0, size: 100 });

  const handleSubmit = async () => {
    setDrawerSendAttachmentEight(false);

    await toast.promise(
      update({
        id: selectOneProject?.id as number,
        manager,
        initialBalance: parseCurrency(initialBalance),
        plannedEndDate: formatDateForBackend(plannedEndDate),
        plannedInitDate: formatDateForBackend(plannedInitDate),
        justify: justification,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('project.settings.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerSendAttachmentEight(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setInitialBalance(formatCurrency(raw));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('settings')} />

        <FormField label={t('common:project') as string} value={selectOneProject?.name as string} disabled />

        <FormFieldSelect
          label={t('manager')}
          value={manager?.id?.toString() ?? ''}
          onChange={(value) => {
            const selected = data?.content.find((item) => item.id.toString() === value);
            setManager(selected as Approver);
          }}
          options={
            data?.content.map((item) => ({
              id: item.id,
              name: t(item.name),
            })) ?? []
          }
          info
        />
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <FormField label={t('initial.balance') as string} value={initialBalance} onChange={handleChange} />

        <Box sx={{ mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="fieldHeader" width={theme.spacing(17)}>
                {t('common:start')}
              </Typography>
              <Stack direction="row" sx={{ width: '28%' }}>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={plannedInitDate ? dayjs(plannedInitDate, 'DD/MM/YYYY') : null}
                  onChange={(newValue: Dayjs | null) =>
                    setPlannedInitDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                  slots={{ openPickerIcon: () => null }}
                />
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography variant="fieldHeader" width={theme.spacing(17)}>
                {t('common:end')}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ width: '28%' }}>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={plannedEndDate ? dayjs(plannedEndDate, 'DD/MM/YYYY') : null}
                  onChange={(newValue: Dayjs | null) =>
                    setPlannedEndDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                  slots={{ openPickerIcon: () => null }}
                />
              </Stack>
            </Box>
          </LocalizationProvider>
        </Box>
        <FormFieldLong
          label={t('common:justification')}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
        />
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} />
      </Box>
    </Box>
  );
}
